import React, { useState, useEffect, useRef } from "react";
import { Button, Col, message, Modal, Row, Tag } from "antd";
import { useParams } from "react-router-dom";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { HolderOutlined } from "@ant-design/icons";
import { ViewFieldOrder } from "redux/models/viewFieldOrder";
import Scrollbars from "react-custom-scrollbars-2";
import { getFieldsSortedByOrder } from "../../../../../../utilities/generalUtility";
import styles from "./EditLayoutModal.module.scss";

const UDFTag = () => (
  <Tag
    color="#1890ff"
    style={{
      borderRadius: 12,
      fontSize: 10,
      lineHeight: "14px",
      padding: "2px 4px",
    }}
  >
    UDF
  </Tag>
);

const EditLayoutModal = ({
  visible,
  onCancel,
  layoutDisplayName,
  layoutConfigKey,
  defaultFields = [],
  fieldRenderer = (field, _) => field.label,
}) => {
  const { id: clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fieldsToDisplay, setFieldsToDisplay] = useState(defaultFields);

  const initialFieldOrder = useRef(fieldsToDisplay.map((f) => f.key));
  const actualFieldOrder = fieldsToDisplay.map((field) => field.key);

  const isChanged =
    JSON.stringify(initialFieldOrder.current) !==
    JSON.stringify(actualFieldOrder);

  const [activeId, setActiveId] = useState(null);

  const activeField = fieldsToDisplay.find((field) => field.key === activeId);

  useEffect(() => {
    const fetchSavedConfiguration = async () => {
      try {
        const { data } = await ViewFieldOrder.getForClientByType(
          clientId,
          layoutConfigKey,
          true,
        );

        if (data?.fieldOrder) {
          const newFields = getFieldsSortedByOrder(
            fieldsToDisplay,
            data?.fieldOrder,
          );

          setFieldsToDisplay(newFields);
          initialFieldOrder.current = newFields.map((f) => f.key);
        }
      } catch (error) {
        console.error("Error fetching saved field order:", error);
      }
    };

    fetchSavedConfiguration();
  }, [clientId, defaultFields]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const fixedFields = fieldsToDisplay.filter((field) => field.fixed);
    const reorderableFields = fieldsToDisplay.filter((field) => !field.fixed);

    const activeIndex = reorderableFields.findIndex(
      (field) => field.key === active.id,
    );
    const overIndex = reorderableFields.findIndex(
      (field) => field.key === over.id,
    );

    if (activeIndex === -1 || overIndex === -1) return;

    const activeField = reorderableFields[activeIndex];

    if (!activeField.isUDF || activeField.fixed) return;

    const newReorderableFields = arrayMove(
      reorderableFields,
      activeIndex,
      overIndex,
    );

    const newFields = [...newReorderableFields, ...fixedFields];

    setFieldsToDisplay(newFields);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const fieldOrder = fieldsToDisplay.map((field) => field.key);

      await ViewFieldOrder.createForClient(
        clientId,
        layoutConfigKey,
        { fieldOrder },
        false,
      );

      message.success(`${layoutDisplayName} layout saved successfully`);
      onCancel();
    } catch (error) {
      console.error("Error saving layout:", error);
      message.error("Failed to save layout");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFieldsToDisplay(defaultFields);
  };

  const SortableItem = ({ field, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: field.key,
      disabled: !field.isUDF || field.fixed,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 1,
      cursor: field.isUDF && !field.fixed ? "move" : "default",
    };

    const dragProps =
      field.isUDF && !field.fixed ? { ...attributes, ...listeners } : {};

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${styles.sortableContainer} ${
          field.isUDF && !field.fixed ? styles.draggable : ""
        }`}
        {...dragProps}
      >
        {children}
      </div>
    );
  };

  const getFieldExtraProps = (field) => {
    const udfFieldProps = {
      extraLabel: (
        <div>
          <UDFTag />
          <HolderOutlined />
        </div>
      ),
    };

    return {
      ...(field.isUDF ? udfFieldProps : {}),
    };
  };

  return (
    <Modal
      title={`Edit ${layoutDisplayName} UDFs Layout`}
      open={visible}
      onCancel={onCancel}
      width={700}
      centered
      className="s2-theme-style"
      closeIcon={<i className="icon-closeable" />}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            type="link"
            onClick={handleReset}
            style={{ color: "#8c8c8c" }}
          >
            Reset to Default
          </Button>
          <div>
            <Button key="cancel" onClick={onCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              key="save"
              type="primary"
              loading={loading}
              disabled={!isChanged}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div>
        <p className={styles.instructions}>
          Preview of {layoutDisplayName} layout. UDF fields are marked with blue
          badges and can be dragged to reposition them after any predefined
          field.
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fieldsToDisplay
              .filter((field) => !field.fixed)
              .map((field) => field.key)}
            strategy={rectSortingStrategy}
          >
            <Scrollbars style={{ height: 600 }}>
              <Row gutter={[8, 8]} style={{ width: "100%" }}>
                {fieldsToDisplay.map((field) => (
                  <Col
                    span={field.span}
                    key={field.key || field.id}
                    className={styles.fieldCol}
                  >
                    <SortableItem field={field}>
                      <div
                        className={`${styles.fieldGroup} ${
                          field.isUDF ? styles.udfFieldGroup : ""
                        } ${
                          activeId === field.key && field.isUDF
                            ? styles.dragging
                            : ""
                        }`}
                      >
                        <div className={styles.fieldContent}>
                          {fieldRenderer(field, getFieldExtraProps(field))}
                        </div>
                      </div>
                    </SortableItem>
                  </Col>
                ))}
              </Row>
            </Scrollbars>
          </SortableContext>

          <DragOverlay>
            {activeField && (
              <div
                className={`${styles.fieldGroup} ${styles.udfFieldGroup} ${styles.dragOverlay}`}
              >
                {fieldRenderer(activeField, getFieldExtraProps(activeField))}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </Modal>
  );
};

export default EditLayoutModal;
